"use client";

import { animate, useInView, useReducedMotion } from "framer-motion";
import { useEffect, useRef } from "react";

const defaultFormat = (n: number) => Math.round(n).toLocaleString("en-US");

/**
 * Counts from 0 → `to` once the element scrolls into view. Honors reduced
 * motion (renders the final value immediately) and re-runs whenever it is
 * remounted, so scroll-revealed scene stats animate each time they appear.
 */
export function CountUp({
  to,
  format = defaultFormat,
  duration = 1.1,
  delay = 0,
  className,
}: {
  to: number;
  format?: (n: number) => string;
  duration?: number;
  delay?: number;
  className?: string;
}) {
  const reduce = useReducedMotion() ?? false;
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (reduce) {
      node.textContent = format(to);
      return;
    }
    if (!inView) {
      node.textContent = format(0);
      return;
    }
    const controls = animate(0, to, {
      duration,
      delay,
      ease: "easeOut",
      onUpdate: (v) => {
        node.textContent = format(v);
      },
    });
    return () => controls.stop();
    // biome-ignore lint/correctness/useExhaustiveDependencies: format is stable inline
  }, [to, inView, reduce, duration, delay]);

  return <span ref={ref} className={className} />;
}
